import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, Instagram, Music2, Twitter, MessageCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import axios from 'axios';
import MetaTags from '@/components/MetaTags';
import { API } from '@/App';
import { toast } from 'sonner';
import { trackContactSubmit } from '@/utils/analytics';
import ConfirmationModal from '@/components/ConfirmationModal';

const Eyebrow = ({ children, light, center }) => (
  <span className={`flex items-center gap-3 font-mono uppercase text-xs tracking-[3px] ${center ? 'justify-center' : ''}`} style={{ color: light ? 'rgba(255,255,255,0.7)' : 'rgba(8,0,16,0.6)' }}>
    <span className="inline-block w-[20px] h-[2px] bg-[#FF2D78]" />
    {children}
  </span>
);

const socialHoverColors = {
  'Instagram': '#FF2D78',
  'TikTok': '#00F5FF',
  'Twitter': '#1E90FF',
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent!');
      trackContactSubmit(formData.subject);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setShowConfirm(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const faqs = [
    { question: 'What is a bar crawl?', answer: 'A bar crawl is a organized nightlife experience where a group visits multiple bars in one night. With CBC, you get VIP access, drink specials, and a guide who knows all the best spots.' },
    { question: 'Do I need to be 21+?', answer: 'Yes, all participants must be 21 or older with a valid government-issued ID. No exceptions - bars are strict about this!' },
    { question: 'What happens if I miss the start time?', answer: 'No worries! Every ticket comes with a bar route card. You can catch up with the group at any stop. Just show your wristband for entry and drink specials.' },
    { question: 'Can I get a refund?', answer: 'Tickets are non-refundable but are transferable. You can give or sell your ticket to someone else - just email us with the new attendee\'s name.' },
    { question: 'What should I wear?', answer: 'Dress to impress! Most crawls are casual, but some rooftop or upscale venues may have dress codes. When in doubt, smart casual works everywhere.' },
    { question: 'Is transportation included?', answer: 'Most crawls are walking distance between bars. For private group bookings, we offer party bus options for an additional fee.' },
  ];

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com', handle: '@crawlbabycrawl' },
    { name: 'TikTok', icon: Music2, href: 'https://tiktok.com', handle: '@crawlbabycrawl' },
    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com', handle: '@crawlbabycrawl' },
  ];

  const messageMaxLength = 500;
  const messageRemaining = messageMaxLength - formData.message.length;

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      <MetaTags
        title="Contact Us — Crawl Baby Crawl"
        description="Questions? Feedback? Just want to say hi? Get in touch with the Crawl Baby Crawl team."
        path="/contact"
      />
      {/* Hero — Cyan (mobile) / Lime (desktop) with watermark */}
      <section className="relative py-20 pb-32 md:pb-20 px-4 text-center overflow-hidden hero-diagonal-clip" style={{ backgroundColor: '#B8FF2E' }} data-testid="contact-hero">
        {/* Mobile-only photo bg + cyan tint */}
        <div className="absolute inset-0 md:hidden">
          <img src="https://customer-assets.emergentagent.com/job_50c259f3-df0a-4de3-8458-52ce4eb888c4/artifacts/ht2t3ncb_SEI_93929522.jpg.webp" alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,245,255,0.60)' }} />
        </div>
        {/* Mobile-only dark text scrim */}
        <div className="absolute inset-0 md:hidden pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(8,0,16,0.4) 0%, rgba(8,0,16,0.15) 60%, transparent 100%)' }} />
        <div className="absolute right-[-40px] top-1/2 -translate-y-1/2 pointer-events-none select-none" aria-hidden="true">
          <span className="font-display text-[140px] md:text-[280px] lg:text-[360px] leading-none uppercase" style={{ color: 'rgba(8,0,16,0.08)' }}>CBC</span>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <span className="flex items-center justify-center gap-3 font-mono uppercase text-xs tracking-[3px] text-white/80 md:text-[rgba(8,0,16,0.6)]">
            <span className="inline-block w-[20px] h-[2px] bg-white md:bg-[#FF2D78]" />
            Get in Touch
          </span>
          <h1 className="font-display text-[48px] md:text-[96px] uppercase mt-2 leading-none font-bold text-white md:text-[#080010]">
            Contact Us
          </h1>
          <p className="text-lg mt-4 max-w-2xl mx-auto font-body text-white/90 md:text-[rgba(8,0,16,0.7)]">
            Questions? Feedback? Just want to say hi? We're here for you.
          </p>
        </motion.div>
      </section>

      <div className="hidden md:block" style={{ height: '5px', backgroundColor: '#080010' }} />

      {/* Contact Grid — Warm off-white */}
      <section className="px-4 py-24" style={{ backgroundColor: '#F5F0EE' }} data-testid="contact-grid-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-[16px] p-8" data-testid="contact-form-card">
              <div className="flex items-center gap-3 mb-6">
                <MessageCircle style={{ color: '#FF2D78' }} size={24} />
                <h2 className="font-display text-[30px] uppercase" style={{ color: '#080010' }}>Send a Message</h2>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Name</label>
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="contact-name-input"
                    className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] rounded"
                    style={{ color: '#080010' }}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="contact-email-input"
                    className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] rounded"
                    style={{ color: '#080010' }}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Subject</label>
                  <input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    data-testid="contact-subject-input"
                    className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] rounded"
                    style={{ color: '#080010' }}
                    placeholder="What's this about?"
                  />
                </div>
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= messageMaxLength) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    data-testid="contact-message-input"
                    className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] min-h-[150px] resize-none rounded"
                    style={{ color: '#080010' }}
                    placeholder="Tell us what's on your mind..."
                    maxLength={messageMaxLength}
                  />
                  <p className="text-right font-mono text-[11px] mt-1" style={{ color: messageRemaining < 50 ? '#FF2D78' : '#999' }} data-testid="char-counter">
                    {messageRemaining} characters remaining
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="w-full py-4 font-display text-lg uppercase tracking-wider rounded-full transition-all duration-150 hover:scale-[1.02] flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#FF2D78', color: '#FFFFFF' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E0005A'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF2D78'}
                >
                  {loading ? 'Sending...' : 'Send Message'} <Send size={18} />
                </button>
              </form>
            </div>
          </motion.div>

          {/* Info & Social */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Contact Info */}
            <div className="bg-white rounded-[16px] p-8" style={{ borderLeft: '5px solid #FF2D78' }} data-testid="contact-info-card">
              <h3 className="font-display text-[24px] uppercase mb-6" style={{ color: '#080010' }}>Other Ways to Reach Us</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,45,120,0.1)' }}>
                    <Mail style={{ color: '#FF2D78' }} size={20} />
                  </div>
                  <div>
                    <p className="text-[#777] text-sm font-body">Email</p>
                    <a href="mailto:hello@crawlbabycrawl.com" className="hover:text-[#FF2D78] transition-colors font-body" style={{ color: '#080010' }}>
                      hello@crawlbabycrawl.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,245,255,0.1)' }}>
                    <MapPin style={{ color: '#00F5FF' }} size={20} />
                  </div>
                  <div>
                    <p className="text-[#777] text-sm font-body">Headquarters</p>
                    <p className="font-body" style={{ color: '#080010' }}>Miami, FL (Nationwide events)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-[16px] p-8" data-testid="contact-social-card">
              <h3 className="font-display text-[24px] uppercase mb-6" style={{ color: '#080010' }}>Follow Us</h3>
              <div className="space-y-3">
                {socialLinks.map((social) => {
                  const hoverColor = socialHoverColors[social.name] || '#B8FF2E';
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${social.name.toLowerCase()}`}
                      className="flex items-center gap-4 p-3 rounded-lg transition-all duration-200 group"
                      style={{ borderBottom: '1px solid #E5E5E5', borderLeft: '3px solid transparent' }}
                      onMouseEnter={(e) => e.currentTarget.style.borderLeftColor = hoverColor}
                      onMouseLeave={(e) => e.currentTarget.style.borderLeftColor = 'transparent'}
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F5F0EE' }}>
                        <social.icon size={18} className="text-[#555]" />
                      </div>
                      <div>
                        <p className="font-bold font-body" style={{ color: '#080010' }}>{social.name}</p>
                        <p className="text-[#999] text-sm font-body">{social.handle}</p>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div style={{ height: '5px', backgroundColor: '#080010' }} />

      {/* FAQ Section — Hot Pink */}
      <section className="px-4 py-24" style={{ backgroundColor: '#FF2D78' }} data-testid="faq-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Eyebrow light center>Got Questions?</Eyebrow>
            <h2 className="font-display text-[40px] md:text-[58px] lg:text-[67px] text-white uppercase mt-2 leading-none">
              FAQ
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="bg-white rounded-[12px] px-6 border-0"
                style={{ borderBottom: '2px solid rgba(255,45,120,0.15)' }}
              >
                <AccordionTrigger
                  className="text-left text-lg font-body hover:no-underline"
                  style={{ color: '#080010' }}
                  data-testid={`faq-trigger-${index}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#555] text-base font-body">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <ConfirmationModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        headline="We Got You."
        subtext="Someone from the CBC crew will hit you back within 24 hours. Keep an eye on your inbox."
      />
    </div>
  );
};

export default ContactPage;
